import React, { useContext, useState } from 'react';
import {
    CheckboxInput,
    Dropdown,
    TimeInput,
} from '@instinet-analytics/semantic-ui-components';
import { Time } from '@instinet-analytics/core-lib';
import useDebouncedConfigInputs from 'utils/hooks/config';
import { PortfolioReturnsComponentState } from '../store/component/PortfolioReturnsComponentState';
import { PortfolioReturnsConfigurationState } from '../store/configuration/PortfolioReturnsConfigurationState';
import './PortfolioReturnsSettings.scss';
import AppContext from 'app/AppContext';
import 'appToolbar/SettingsModal.scss';
import AppSettingsEditor from 'appToolbar/AppSettingsEditor';
import { useSelectIsAppModeNewportSingleWidget } from 'app/store/general/selectors';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { useDispatchUpdateAppSettings } from 'app/store/appSettings/dispatcher';
import { RadioGroup } from '@instinet-analytics/semantic-ui-components';
import SymbolInput from 'symbols/components/SymbolInput';
import {
    PriceMovementVersusType,
    usePortfolioReturnsPriceMovementVersus,
} from 'widgets/portfolioReturns/usePortfolioReturnsPriceMovementVersus';
import WidgetSettingsCheckboxValueEditor from 'widgets/WidgetSettingsEditing/WidgetSettingsCheckboxValueEditor';
import { BreaksRenderType } from 'utils/components/TimeSeriesChart/timebreaks';
import { PortfolioReturnsSettingsDateRange } from 'widgets/portfolioReturns/settings/PortfolioReturnsSettingsDateRange';

const NoBasisDays = -1;

export interface PortfolioReturnsSettingsProps {
    component: PortfolioReturnsComponentState;
    config: PortfolioReturnsConfigurationState;
    availableBasisDays: Date[];
    onComponentChange: (
        component: Partial<PortfolioReturnsComponentState>
    ) => void;
    onConfigChange: (
        component: Partial<PortfolioReturnsConfigurationState>
    ) => void;
}

export default function PortfolioReturnsSettings(
    props: PortfolioReturnsSettingsProps
) {
    const { timeFormat } = useContext(AppContext);
    const {
        component,
        config,
        availableBasisDays,
        onComponentChange,
        onConfigChange,
    } = props;

    const { basisTime, basisDay } = component;

    const isAppModeNewportSingleWidget =
        useSelectIsAppModeNewportSingleWidget();
    const appSettings = useSelectAppSettings();
    const dispatchUpdateAppSettings = useDispatchUpdateAppSettings();

    const { priceMovementVersus } = usePortfolioReturnsPriceMovementVersus();

    const { index1, index2, onValueChange } =
        useDebouncedConfigInputs<PortfolioReturnsConfigurationState>(
            {
                index1: config.index1,
                index2: config.index2,
            },
            onConfigChange
        );

    const [showMarketSessionTimes, setShowMarketSessionTimes] = useState(
        config.showMarketSessionTimes
    );
    const [breaks, setBreaks] = useState(config.breaks ?? 'default');

    const [priceMovementVesus, setPriceMovementVersus] =
        useState<PriceMovementVersusType>(priceMovementVersus);

    const basisDayDropdownValues = [
        { text: '(none)', value: NoBasisDays },
        ...availableBasisDays.map((d) => ({
            text: timeFormat.toDate(d.getTime()),
            value: d.getTime(),
        })),
    ];

    const basisDayValue =
        basisDay === undefined ? NoBasisDays : basisDay.getTime();

    const onToggleShowMarketSessionTimes = () => {
        const newValue = !config.showMarketSessionTimes;
        setShowMarketSessionTimes(newValue);
        const configDelta: Partial<PortfolioReturnsConfigurationState> = {
            showMarketSessionTimes: newValue,
        };
        onConfigChange(configDelta);
    };

    const onBasisTimeChanged = (time?: Time) => {
        const delta = { basisTime: time };
        onComponentChange(delta);
    };

    const onBasisDayChanged = (time?: number) => {
        const date = time && time !== NoBasisDays ? new Date(time) : undefined;
        const delta = { basisDay: date };
        onComponentChange(delta);
    };

    const onPriceMovementVersusChange = (key: PriceMovementVersusType) => {
        if (key !== 'specificDate') {
            onComponentChange({ basisTime: undefined, basisDay: undefined });
            onValueChange('useAvgExecPrice', key === 'avgExec');
        }
        setPriceMovementVersus(key);
    };

    const onBreaksChange = (nextBreak: BreaksRenderType) => {
        setBreaks(nextBreak);
        const configDelta: Partial<PortfolioReturnsConfigurationState> = {
            breaks: nextBreak,
        };
        onConfigChange(configDelta);
    };

    const onIndex1Changed = (nextValue: string) =>
        onValueChange('index1', nextValue);
    const onIndex2Changed = (nextValue: string) =>
        onValueChange('index2', nextValue);

    const invertSellLineLabel = appSettings.combineSellsAndShorts
        ? 'Invert sell P&L Line?'
        : 'Invert sell/short P&L Lines?';

    return (
        <div className="PortfolioReturnsSettings">
            <div className="group">
                <div className="index1">
                    <div>Comparison Index or Symbol</div>
                    <SymbolInput
                        value={index1 ?? ''}
                        onChange={onIndex1Changed}
                    />
                </div>
                <div className="index2">
                    <div>Secondary Index or Symbol</div>
                    <SymbolInput
                        value={index2 ?? ''}
                        onChange={onIndex2Changed}
                    />
                </div>
            </div>
            <div className="group">
                <div className="show-market-session-times">
                    <CheckboxInput
                        label="Show Market Session Times"
                        checked={showMarketSessionTimes}
                        onToggle={onToggleShowMarketSessionTimes}
                    />
                </div>
            </div>
            <div className="group">
                <div>Price Movement Versus</div>
                <RadioGroup
                    selected={priceMovementVesus}
                    onChange={onPriceMovementVersusChange}
                    options={[
                        {
                            key: 'prevClose',
                            label: 'Previous Close (as of end date)',
                        },
                        {
                            key: 'avgExec',
                            label: 'Average Execution Price',
                        },
                        {
                            key: 'specificDate',
                            label: 'Specific Date and Time',
                        },
                    ]}
                />
                {priceMovementVesus === 'specificDate' && (
                    <>
                        <div className="horizontal basis-days">
                            <div>Date</div>
                            <div>
                                <Dropdown
                                    className="SpecificDateDropdown"
                                    options={basisDayDropdownValues}
                                    value={basisDayValue}
                                    onChange={onBasisDayChanged}
                                />
                            </div>
                        </div>

                        <div className="horizontal basis-time">
                            <div>Time</div>
                            <TimeInput
                                className="basis-time"
                                time={basisTime}
                                onChange={onBasisTimeChanged}
                            />
                        </div>
                    </>
                )}
            </div>

            <PortfolioReturnsSettingsDateRange
                component={component}
                days={config.days}
                onComponentChange={onComponentChange}
                onConfigChange={onConfigChange}
            />

            <div className="group">
                <div>Breaks in Trading</div>
                <RadioGroup
                    selected={breaks ?? 'default'}
                    onChange={onBreaksChange}
                    className={'breaks-in-trading'}
                    options={[
                        {
                            key: 'default',
                            label: 'Rectangles',
                        },
                        {
                            key: 'line',
                            label: 'Lines',
                        },
                        {
                            key: 'hide',
                            label: 'Hide',
                        },
                    ]}
                />
            </div>
            <div className="group">
                <WidgetSettingsCheckboxValueEditor
                    propertyName={'hideTitlePanel'}
                    config={config}
                    label="Hide title panel?"
                    onChange={onConfigChange}
                />
                <WidgetSettingsCheckboxValueEditor
                    propertyName={'invertSellLine'}
                    config={config}
                    label={invertSellLineLabel}
                    onChange={onConfigChange}
                />
            </div>
            {isAppModeNewportSingleWidget && (
                <AppSettingsEditor
                    appSettings={appSettings}
                    onAppSettingsChanged={dispatchUpdateAppSettings}
                    specifyVisibleControls={{
                        currency: true,
                        symbology: true,
                        combineSellsAndShorts: true,
                        showChartHelp: true,
                    }}
                />
            )}
        </div>
    );
}
